import config from '../config';
import { FRONTEND_VERSION } from '../constants';
import ApiClient from '../redux/ApiClient';

// Re-export for convenience
export { FRONTEND_VERSION };

// Create API client instance for authenticated requests
const api = new ApiClient();

/**
 * Get the current version from the server
 * Returns null on any error - should never break the app
 */
export const getCurrentVersion = async () => {
  try {
    const response = await fetch(`${config.apiHost}/version/current`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    // Silently fail - version check should never break the app
    return null;
  }
};

/**
 * Check if the client version is up to date
 */
export const checkVersion = async () => {
  try {
    const response = await fetch(
      `${config.apiHost}/version/check?frontendVersion=${FRONTEND_VERSION}`
    );
    if (!response.ok) {
      throw new Error('Failed to check version');
    }
    return await response.json();
  } catch (error) {
    console.error('Error checking version:', error);
    return null;
  }
};

/**
 * Check version and return whether update is needed
 * @returns {Promise<{needsUpdate: boolean, message: string}>}
 */
export const checkForUpdate = async () => {
  const result = await checkVersion();
  
  if (!result) {
    return { needsUpdate: false, message: '' };
  }
  
  return {
    needsUpdate: !result.isUpToDate,
    message: result.message,
    currentVersion: result.currentVersion,
    clientVersion: result.clientVersion
  };
};

/**
 * Store last version check timestamp
 */
const VERSION_CHECK_KEY = 'lastVersionCheck';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Check if we should perform a version check based on last check time
 */
export const shouldCheckVersion = () => {
  const lastCheck = localStorage.getItem(VERSION_CHECK_KEY);
  if (!lastCheck) return true;
  
  const timeSinceLastCheck = Date.now() - parseInt(lastCheck, 10);
  return timeSinceLastCheck > VERSION_CHECK_INTERVAL;
};

/**
 * Update the last version check timestamp
 */
export const updateVersionCheckTimestamp = () => {
  localStorage.setItem(VERSION_CHECK_KEY, Date.now().toString());
};

/**
 * Perform version check if needed and return result
 */
export const performVersionCheck = async () => {
  if (!shouldCheckVersion()) {
    return { needsUpdate: false };
  }
  
  const result = await checkForUpdate();
  updateVersionCheckTimestamp();
  
  return result;
};

/**
 * Get version history from the server
 * @param {number} limit - Number of records to fetch
 */
export const getVersionHistory = async (limit = 50) => {
  try {
    const result = await api.get('/version/history', { params: { limit } });
    return result;
  } catch (error) {
    console.error('Error fetching version history:', error);
    return [];
  }
};

/**
 * Add a new version
 * @param {Object} versionData - Version data
 */
export const addVersion = async (versionData) => {
  try {
    const result = await api.post('/version/add', { data: versionData });
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to add version');
  }
};

/**
 * Update a version
 * @param {number} id - Version ID
 * @param {Object} versionData - Version data
 */
export const updateVersion = async (id, versionData) => {
  try {
    const result = await api.put(`/version/${id}`, { data: versionData });
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to update version');
  }
};

/**
 * Delete a version
 * @param {number} id - Version ID
 */
export const deleteVersion = async (id) => {
  try {
    const result = await api.del(`/version/${id}`);
    return result;
  } catch (error) {
    throw new Error(error.message || 'Failed to delete version');
  }
};

const versionUtils = {
  FRONTEND_VERSION,
  getCurrentVersion,
  checkVersion,
  checkForUpdate,
  performVersionCheck,
  shouldCheckVersion,
  updateVersionCheckTimestamp,
  getVersionHistory,
  addVersion,
  updateVersion,
  deleteVersion
};

export default versionUtils;
